import React, { useRef, useEffect, useState, useMemo } from "react";
import {
  LessonCard,
  LearningOutcomesContent,
  KnowledgeCheckContent,
  QuickQuestionContent,
  InformationContent,
  VideoContent,
  QuickRecapContent,
  MatchExerciseContent,
  DataTableContent,
  DataVisualizationContent,
  FlashcardContent,
  RevealTextStripContent,
  ImageHotspotContent,
  SequenceSortContent,
  DragAndDropSorterContent,
  PollContent,
} from "../../types/newtypes";
import {
  OutcomesPreview,
  InformationPreview,
  VideoPreview,
  QuickRecapPreview,
  DataTablePreview,
  QuickQuestionPreview,
  KnowledgeCheckPreview,
  MatchExercisePreview,
  DataVisualizationPreview,
  FlashcardPreview,
  RevealTextStripPreview,
  ImageHotspotPreview,
  SequenceSortPreview,
  DragAndDropSorterPreview,
} from "./cards";
import Poll from "./cards/non-interactive/Poll";
import { ChevronDown, ChevronUp } from "lucide-react";
import { normalizeContentQuotes } from "../../utils/normalizeQuotes";

export interface CardWrapperProps {
  card: LessonCard;
  onScoreChanged?: (score: number) => void;
  onCompletedSection?: () => void;
  isLastSection?: boolean;
  onCardStateChange?: (isCompleted: boolean, canProceed: boolean) => void;
  lessonId?: string;
}

const CardWrapper: React.FC<CardWrapperProps> = ({
  card,
  onScoreChanged,
  onCompletedSection,
  isLastSection,
  onCardStateChange,
  lessonId,
}) => {
  // Ref for the scrollable card container
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);

  // Scroll to top when card changes
  useEffect(() => {
    if (cardContainerRef?.current) {
      cardContainerRef?.current?.scrollTo({
        top: 0,
        behavior: "auto",
      });
    }
  }, [card?.id]);

  // Check if scroll indicators should be shown
  useEffect(() => {
    const checkScrollIndicators = () => {
      if (cardContainerRef?.current) {
        const { scrollTop, scrollHeight, clientHeight } =
          cardContainerRef?.current;
        const hasMoreContent = scrollHeight > clientHeight;
        const isNotAtBottom = scrollTop < scrollHeight - clientHeight - 10; // 10px threshold
        const hasScrolledDown = scrollTop > 100; // Show scroll-to-top after 100px

        setShowScrollIndicator(hasMoreContent && isNotAtBottom);
        setShowScrollToTop(hasMoreContent && hasScrolledDown);
      }
    };

    // Check after a short delay to ensure content is rendered
    const timer = setTimeout(checkScrollIndicators, 100);

    // Also check on scroll
    const handleScroll = () => {
      checkScrollIndicators();
    };
    
    const cardElement = cardContainerRef.current;
    if (cardElement) {
      cardElement.addEventListener("scroll", handleScroll);
    }

    return () => {
      clearTimeout(timer);
      if (cardElement) {
        cardElement.removeEventListener("scroll", handleScroll);
      }
    };
  }, [card?.id]);

  // Handle scroll down button click
  const handleScrollDown = () => {
    if (cardContainerRef?.current) {
      const currentScrollTop = cardContainerRef?.current?.scrollTop;
      const scrollAmount = 300; // Scroll by 300px

      cardContainerRef?.current?.scrollTo({
        top: currentScrollTop + scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Handle scroll to top button click
  const handleScrollToTop = () => {
    if (cardContainerRef?.current) {
      cardContainerRef?.current?.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // Normalise all string fields in the card content so that every rendering
  // path (MarkdownWrapper *and* plain Typography) gets proper directional
  // typographic quotes instead of ambiguous straight ASCII quotes.
  // Memoised on card.id so interactive-card hooks that key on content
  // reference (e.g. useMatchExercisePreview, useDragAndDropSorterHook)
  // receive a stable object and do not re-shuffle on every scroll event.
  const normalizedContent = useMemo(
    () => normalizeContentQuotes(card?.content),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [card?.id],
  );

  const renderCard = () => {
    const content = normalizedContent;

    switch (card?.type) {
      case "learning_outcomes":
        return (
          <OutcomesPreview
            key={card?.id}
            content={content as LearningOutcomesContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "knowledge_check":
        return (
          <KnowledgeCheckPreview
            key={card?.id}
            cardId={card?.id}
            content={content as KnowledgeCheckContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "quick_question":
        return (
          <QuickQuestionPreview
            key={card?.id}
            content={content as QuickQuestionContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "information":
        return (
          <InformationPreview
            key={card?.id}
            content={content as InformationContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "video":
        return (
          <VideoPreview
            key={card?.id}
            content={content as VideoContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "quick_recap":
        return (
          <QuickRecapPreview
            key={card?.id}
            content={content as QuickRecapContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "match_exercise":
        return (
          <MatchExercisePreview
            key={card?.id}
            cardId={card?.id}
            content={content as MatchExerciseContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "data_table":
        return (
          <DataTablePreview
            key={card?.id}
            content={content as DataTableContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "data_visualization":
        return (
          <DataVisualizationPreview
            key={card?.id}
            cardId={card?.id}
            content={content as DataVisualizationContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "sequence_sort":
        return (
          <SequenceSortPreview
            key={card?.id}
            cardId={card?.id}
            content={content as SequenceSortContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "drag_and_drop_sorter":
        return (
          <DragAndDropSorterPreview
            key={card?.id}
            cardId={card?.id}
            content={content as DragAndDropSorterContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "flashcard":
        return (
          <FlashcardPreview
            key={card?.id}
            content={content as FlashcardContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "reveal_text_strip":
        return (
          <RevealTextStripPreview
            key={card?.id}
            content={content as RevealTextStripContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "image_hotspot":
        return (
          <ImageHotspotPreview
            key={card?.id}
            content={content as ImageHotspotContent}
            onCompleted={onCompletedSection}
            onCardStateChange={onCardStateChange}
          />
        );
      case "assessment":
        return (
          <div
            role="alert"
            className="mx-auto max-w-2xl rounded-xl border border-amber-300 bg-amber-50 p-6 text-amber-950"
          >
            <h2 className="mb-2 text-lg font-semibold">
              Standalone assessment retired
            </h2>
            <p>
              This course contains retired content and cannot continue here.
              Active course evaluation uses scored activities within units.
            </p>
          </div>
        );
      case "poll":
        return (
          <Poll
            key={card?.id}
            content={content as PollContent}
            lessonId={lessonId}
            onCardStateChange={onCardStateChange}
          />
        );
      default:
        return (
          <div className="text-center py-8">
            <div className="text-gray-600 mb-4">
              Card type "{card?.type}" is not yet supported
            </div>
            <div className="text-sm text-gray-500">
              Content: {JSON.stringify(card?.content, null, 2)}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="relative w-full h-full">
      <div
        ref={cardContainerRef}
        data-testid="lesson-card-scroll-container"
        className="w-full h-full pb-12 overflow-y-auto max-h-[calc(100vh-200px)]"
      >
        {renderCard()}
      </div>

      {/* Scroll Down Indicator */}
      {showScrollIndicator && (
        <div className="absolute bottom-4 right-4 z-10">
          <button
            onClick={handleScrollDown}
            className="bg-white hover:bg-gray-50 border border-gray-200 rounded-full p-3 shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
            title="Scroll down to see more content"
          >
            <ChevronDown className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <div className="absolute bottom-4 right-4 z-10">
          <button
            onClick={handleScrollToTop}
            className="bg-white hover:bg-gray-50 border border-gray-200 rounded-full p-3 shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-105"
            title="Scroll to top"
          >
            <ChevronUp className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      )}
    </div>
  );
};

export default CardWrapper;
